import { useEffect, useState } from "react";
import useErrorHandler from "../FormErrorHandler";
import {
  FormDetails,
  FormSection,
  CheckboxInput,
  FormHeader,
} from "../../FormInput/FormInput";
import { getDisclosureAgreementText } from "../DisclousureAgreement";

const Confirm = ({ formData, handleInputChange, validateForm }) => {
  const { error, addError, clearError, hasError } = useErrorHandler();
  const [reporterAddress, setReporterAddress] = useState("");
  const [driverAddress, setDriverAddress] = useState("");
  const [lossAddress, setLossAddress] = useState("");
  const [injuryAddresses, setInjuryAddress] = useState([]);

  const validate = (formData) => {
    clearError();

    if (!formData.confirmSelections || formData.confirmSelections.length == 0) {
      addError({ confirmSelections: "This field is required" });
    }

    return !hasError();
  };

  validateForm.current = validate;

  const formatAddress = (data) => {
    if (!data || !data?.StreetAddress || !data?.City || !data?.ZipCode) return "";

    let address = `${data.StreetAddress}${
      data.ApartmentNo ? " " + data.ApartmentNo : ""
    } ${data.City}, ${data.State ? " " + data.State : ""} ${data.ZipCode}`;

    return address;
  };

  const updateInjuryAddresses = async () => {
    const updatedAddresses = formData.additionalInfo.injuredPersonsInfo.map(
      async (person) => {
        const address = formatAddress(person);
        return { address, id: person.id };
      }
    );
    setInjuryAddress(updatedAddresses);
  };

  const formatDate = (date) => {
    date = new Date(date);
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatTime = (date) => {
    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Use 24-hour format
    });

    return timeFormatter.format(date);
  };

  const formatName = (firstName, lastName) => (firstName ? firstName : '') + (lastName ? ' ' + lastName : '');
  const involvedDriver =
  formData.involvedDriverIndex && formData.involvedDriverIndex != "other"
      ? formData.drivers[formData.involvedDriverIndex]
      : formData.cureDriverInfo;
      
  const involvedDriverDisplay = (
    <div>
      {formData.involvedDriverIndex != "other" ? (
        involvedDriver.fullName
      ) : (
        <>
          {involvedDriver.FirstName} {involvedDriver.LastName}
        </>
      )}
      {involvedDriver.DOB && (
        <>
          &nbsp;-{" "}
          <span className="fw-normal">
            DOB ({formatDate(involvedDriver.DOB)})
          </span>
        </>
      )}
    </div>
  );

  const involvedVehicle =
  formData.involvedVehicleIndex && formData.involvedVehicleIndex != "other"
      ? {
          VIN: formData.vehicles[formData.involvedVehicleIndex]
            .vehidentificationnumber,
          year: formData.vehicles[formData.involvedVehicleIndex].modelyr,
          make: formData.vehicles[formData.involvedVehicleIndex].manufacturer,
          model: formData.vehicles[formData.involvedVehicleIndex].model,
        }
      : formData.vehicleInfo;
  const involvedVehicleDisplay = (
    <div>
      {involvedVehicle.year} {involvedVehicle.make} {involvedVehicle.model}
      {involvedVehicle.VIN && (
        <>
          &nbsp;- <span className="fw-normal">({involvedVehicle.VIN})</span>
        </>
      )}
    </div>
  );

  const confirmDetails = [
    {
      header: "Loss Date and Time",
      content:
        formatDate(formData.lossInfo.DateOfLoss) +
        " at " +
        formatTime(formData.lossInfo.TimeOfLoss),
    },
    {
      header: "Who is reporting this loss?",
      content: formatName(
        formData.user.FirstName,
        formData.user.LastName
      ),
    },
    {
      header: "Relationship to Policyholder",
      content: formData.user.RelationToPolicyHolder,
    },
    { header: "Phone Number", content: formData.user.PhoneNumber },
    { header: "Email", content: formData.user.EmailAddress },
    { header: "Home Address", content: formatAddress(formData.user) },
    {
      header: "Driver Involved in Loss",
      content: involvedDriverDisplay,
    },
    {
      header: "Relationship to Insured",
      content: formData.cureDriverInfo.relationToPolicyHolder,
    },
    {
      header: "Driver's License Number",
      content: formData.cureDriverInfo.DLNumber,
    },
    {
      header: "Driver's License State",
      content: formData.cureDriverInfo.DLState,
    },
    {
      header: "Email",
      content: formData.cureDriverInfo.EmailAddress,
    },
    {
      header: "Phone Number",
      content: formData.cureDriverInfo.PhoneNumber,
    },
    {
      header: "Home Address",
      content: formatAddress(formData.cureDriverInfo),
    },
    {
      header: "Vehicle Involved in Loss",
      content: involvedVehicleDisplay,
    },
    {
      header: "Vehicle Color",
      content: formData.vehicleInfo.color,
    },
    {
      header: "Vehicle Plate #",
      content: formData.vehicleInfo.plateNumber,
    },
    {
      header: "Vehicle Mileage",
      content: formData.vehicleInfo.mileage,
    },
    {
      header: "Vehicle Owner",
      content: formData.vehicleInfo.owner,
    },
    {
      header: "Who is this vehicle registered to?",
      content: formData.vehicleInfo.registeredTo,
    },
    {
      header:
        "Was there any prior damage to this vehicle unrelated to this loss?",
      content: formData.vehicleInfo.priorDamage,
    },
    {
      header:
        "Prior Damage Desciption:",
      content: formData.vehicleInfo.priorDamageDescription,
    },
    {
      header: "Address of Loss Location",
      content: formatAddress(formData.lossInformation),
    },
    {
      header: "Describe in detail how this loss occurred:",
      content: formData.lossInformation.descriptionOfAccident,
    },
    {
      header: "Did the police come to the scene?",
      content: formData.lossInformation.policeContacted,
    },
    {
      header: "Police District/Township",
      content: formData.lossInformation.policeDistrict,
    },
    {
      header: "Case/Report Number",
      content: formData.lossInformation.reportNumber,
    },
    {
      header: "Please describe the damage to your vehicle:",
      content: formData.vehicleInfo.describeDamage,
    },
    {
      header: "Did the vehicle airbags deploy?",
      content: formData.vehicleInfo.isAirbagDeployed,
    },
    {
      header: "Is the vehicle in a condition where it cannot be driven?",
      content: formData.vehicleInfo.isDrivable,
    },
    {
      header: "Describe why it cannot be driven",
      content: formData.vehicleInfo.driveableDescription,
    },
    {
      header:
        "Is the roof of this vehicle buckled and/or did the vehicle roll over?",
      content: formData.vehicleInfo.isRoofBuckled,
    },
    {
      header: "Was this vehicle towed?",
      content: formData.vehicleInfo.isTowed,
    },
    {
      header: "Tow location information",
      content: formData.vehicleInfo.towedLocation,
    },
    {
      header: "Do you have a shop of choice?",
      content: formData.vehicleInfo.repairShopInd,
    },
    {
      header: "Repair Shop Name",
      content: formData.vehicleInfo.repairShopName,
    },
    {
      header: "Repair Shop Phone Number",
      content: formData.vehicleInfo.repairShopPhoneNumber,
    },
    {
      header: "Repair Shop Address",
      content: formatAddress(formData.vehicleInfo.repairShopAddress),
    },
    {
      header: "Where there any other vehicles or property damaged in the loss?",
      content: formData.anyOtherVehiclesInvolved,
    },
    {
      header: "Were there any passengers in your vehicle?",
      content: formData.wereThereAnyPassengersInVehicle,
    },
  ];

  const passengerDetails = (data) => [
    {
      header: "Full Name",
      content: formatName(data.FirstName, data.LastName),
    },
    {
      header: "Phone Number",
      content: data.PhoneNumber,
    },
    {
      header: "Home Address",
      content: formatAddress(data)
    },
    {
      header: "Was this passenger injured?",
      content: data.Injured
    },
    {
      header: "Please describe any injuries this passenger sustained in the loss:",
      content: data.InjuryDescription
    },
  ];

  const injuryDetails = (data) => [
    {
      header: "Full Name",
      content: formatName(data.FirstName, data.LastName),
    },
    {
      header: "Phone Number",
      content: data.PhoneNumber,
    },
    {
      header: "Home Address",
      content: formatAddress(data)
    },
    {
      header: "Please describe any injuries this person sustained in the loss:",
      content: data.description,
    },
  ];

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const headerText = isMobile ? "CONFIRM YOUR INFORMATION" : "CONFIRM YOUR CLAIM INFORMATION"

  return (
    <div>
      <FormHeader>{headerText}</FormHeader>
      {confirmDetails.map(
        (details, index) =>
          details.content &&
          details.content != "" && (
            <FormDetails key={index} header={details.header} content={details.content} />
          )
      )}
      {formData.passengersInvehiclesLists &&
        formData.passengersInvehiclesLists.map((person, index) => (
          <FormSection header={`Passenger ${index + 1}`}>
            {passengerDetails(person).map(
              (details) =>
                details.content &&
                details.content != "" && (
                  <FormDetails
                    header={details.header}
                    content={details.content}
                  />
                )
            )}
          </FormSection>
        ))}
      <FormDetails header="Were any other individuals injured in this loss?" content={formData.wereThereAnyInjuriesInLoss} />
      {formData.injuriesInLossLists &&
        formData.injuriesInLossLists.map((person, index) => (
          <FormSection header={`Injured Person ${index + 1}`}>
            {injuryDetails(person).map(
              (details) =>
                details.content &&
                details.content != "" && (
                  <FormDetails
                    header={details.header}
                    content={details.content}
                  />
                )
            )}
          </FormSection>
        ))}
      <hr />
      <FormHeader>CONFIRM SELECTIONS</FormHeader>
      <CheckboxInput
        fieldName="confirmSelections"
        value={formData.confirmSelections}
        handleInputChange={handleInputChange}
        options={[
          {
            label:
              "I hereby certify that the answers to the questions listed in the claim are true and accurate to the best of my knowledge.*",
            value: "checked",
          },
        ]}
        error={error}
      />
      <p>{getDisclosureAgreementText(formData.state)}</p>
      <hr />
      <div className="text-center">
        <i>To make any changes to your above selections, click BACK.</i>
      </div>
    </div>
  );
};

export default Confirm;
